/**
 * mutationHandlers.js — Per-type registry for optimistic UI and rollback.
 *
 * layer values:
 *   OVERLAY          — read-time overlay (no Dexie write for optimism).
 *                      applyOptimistic is null; state is derived by overlay
 *                      functions in db/overlay.js.
 *   OPTIMISTIC_WRITE — writes an ephemeral entity to Dexie immediately so
 *                      the UI renders it before the server confirms.
 *
 * rollback(tx, payload, prior_state) — per-type function that reverses the
 *   optimistic entity change. Called by discardMutation inside a Dexie rw
 *   transaction. prior_state may be undefined; all rollbacks guard for it.
 *   Overlay types have a no-op rollback (log-row delete is the full undo).
 */

export const MUTATION_LAYER = {
  OVERLAY: 'overlay',
  OPTIMISTIC_WRITE: 'optimistic_write',
};

// --- optimistic write functions ---

async function applyFeedback(tx, payload) {
  const row = await tx.table('articles').get(payload.article_id);
  if (!row) return; // article not cached locally — server settles later
  const prior = row.payload?.user_feedback ?? null;
  await tx.table('articles').update(payload.article_id, {
    payload: { ...row.payload, user_feedback: payload.feedback },
  });
  return { prior_state: { user_feedback: prior } };
}

async function applyHighlightCreate(tx, payload, key) {
  const localId = -(Date.now() * 1000 + Math.floor(Math.random() * 1000));
  await tx.table('highlights').put({
    id: localId,
    article_id: payload.article_id,
    annotation: payload.annotation,
    exact_text: payload.exact_text,
    summary_version: payload.summary_version,
    fetched_at: Date.now(),
    pending_key: key,
  });
  return { prior_state: { local_id: localId } };
}

async function applyHighlightDelete(tx, payload) {
  const row = await tx.table('highlights').get(payload.highlight_id);
  if (!row) return;
  await tx.table('highlights').delete(payload.highlight_id);
  return { prior_state: { row } };
}

async function applyDigestHighlightCreate(tx, payload, key) {
  const localId = -(Date.now() * 1000 + Math.floor(Math.random() * 1000));
  await tx.table('highlights').put({
    id: localId,
    article_id: payload.article_id ?? null,
    topic_name: payload.topic_name,
    digest_section: payload.digest_section ?? null,
    digest_text: payload.digest_text,
    digest_version: payload.digest_version,
    fetched_at: Date.now(),
    pending_key: key,
  });
  return { prior_state: { local_id: localId } };
}

// --- rollback functions ---

// Overlay: no entity write; deleting the log row (done by discardMutation)
// makes the overlay disappear on next render.
async function rollbackOverlay() {
  // intentional no-op
}

async function rollbackFeedback(tx, payload, prior_state) {
  if (!prior_state) return;
  const row = await tx.table('articles').get(payload.article_id);
  if (!row) return; // article evicted from local cache — nothing to undo
  await tx.table('articles').update(payload.article_id, {
    payload: { ...row.payload, user_feedback: prior_state.user_feedback },
  });
}

async function rollbackHighlightCreate(tx, _payload, prior_state) {
  if (!prior_state) return;
  // Reason: get() by the negative local_id returns nothing if the server
  // already confirmed and stored a new positive-id row — safe to skip.
  const existing = await tx.table('highlights').get(prior_state.local_id);
  if (!existing) return;
  await tx.table('highlights').delete(prior_state.local_id);
}

async function rollbackHighlightDelete(tx, _payload, prior_state) {
  if (!prior_state?.row) return;
  // Idempotent put: re-inserts the previously deleted row.
  await tx.table('highlights').put(prior_state.row);
}

async function rollbackDigestHighlightCreate(tx, _payload, prior_state) {
  if (!prior_state) return;
  const existing = await tx.table('highlights').get(prior_state.local_id);
  if (!existing) return;
  await tx.table('highlights').delete(prior_state.local_id);
}

export const MUTATION_HANDLERS = {
  // Overlay-layer: is_read flag is derived at read time from the mutation log.
  mark_read: {
    layer: MUTATION_LAYER.OVERLAY,
    applyOptimistic: null,
    rollback: rollbackOverlay,
  },
  mark_unread: {
    layer: MUTATION_LAYER.OVERLAY,
    applyOptimistic: null,
    rollback: rollbackOverlay,
  },
  mark_all_read: {
    layer: MUTATION_LAYER.OVERLAY,
    applyOptimistic: null,
    rollback: rollbackOverlay,
  },

  // Optimistic-write-layer: writes entity to Dexie immediately.
  feedback: {
    layer: MUTATION_LAYER.OPTIMISTIC_WRITE,
    applyOptimistic: applyFeedback,
    rollback: rollbackFeedback,
  },
  highlight_create: {
    layer: MUTATION_LAYER.OPTIMISTIC_WRITE,
    applyOptimistic: applyHighlightCreate,
    rollback: rollbackHighlightCreate,
  },
  highlight_delete: {
    layer: MUTATION_LAYER.OPTIMISTIC_WRITE,
    applyOptimistic: applyHighlightDelete,
    rollback: rollbackHighlightDelete,
  },
  digest_highlight_create: {
    layer: MUTATION_LAYER.OPTIMISTIC_WRITE,
    applyOptimistic: applyDigestHighlightCreate,
    rollback: rollbackDigestHighlightCreate,
  },
};
